import { useEffect } from "react";
import { createRoutesFromChildren, matchRoutes, Routes, useLocation, useNavigationType } from "react-router-dom";
import * as Sentry from "@sentry/react";

// Get environment from build-time variable or detect locally
const isProduction = process.env.NODE_ENV === "production";
const environment = isProduction ? import.meta.env.VITE_ENVIRONMENT : "development";
const release = import.meta.env.VITE_RELEASE_VERSION || "development";

let apiUrlAppendix = `-${import.meta.env.VITE_ENVIRONMENT}`;

if (import.meta.env.VITE_ENVIRONMENT === "prod") {
    apiUrlAppendix = "";
}

// Firefox words a rejected fetch "TypeError: NetworkError when attempting to fetch resource." — noise,
// unless the event carries a support code: utils/error.ts reportError tags `supportCode` exactly when
// the client was shown "kód XXXXXXXX", and that code must resolve to an event in Sentry. It lives here
// and not in `ignoreErrors` because the SDK applies `ignoreErrors` before `beforeSend` and would drop
// the event before this function could look at its tags.
const FIREFOX_FETCH_FAILURE = /NetworkError when attempting to fetch resource/i;

export const filterEvent = (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
    if (event.tags?.supportCode) {
        return event;
    }

    const texts = [event.message, ...(event.exception?.values ?? []).map((value) => value.value)];

    return texts.some((text) => text && FIREFOX_FETCH_FAILURE.test(text)) ? null : event;
};

Sentry.init({
    dsn: "https://9865faaf9ae893cc613761e88c58814f@o1067152.ingest.us.sentry.io/4509678681849856",
    environment,
    release,
    enabled: isProduction,
    initialScope: {
        tags: { component: "client-ui" },
        contexts: {
            deployment: { hostname: window.location.hostname, origin: window.location.origin },
        },
    },
    integrations: (defaultIntegrations) => [
        ...defaultIntegrations,
        // See docs for support of different versions of variation of react router
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
        /^\//, // Relative URLs (same origin)
        new RegExp(`^https:\\/\\/api-client${apiUrlAppendix}\\.testuj\\.to`),
        // not restapi-client: the REST API does not allow the Sentry tracing headers
    ],
    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // results in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
        // Validation and known benign errors we don't want to alert on
        /ResizeObserver loop limit exceeded/i,
    ],
    beforeSend: filterEvent,
    denyUrls: [
        // Chrome extensions etc.
        /extensions\//i,
        /^chrome:\/\//i,
    ],
});

export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
